import React, { useCallback, useEffect, useRef, useState } from 'react';
import PasswordModal from './PasswordModal';
import { verifyPassword } from '../utils/passwordUtils';
import { ReactComponent as IconLock } from '../image/icon_lock.svg';

export interface ProtectedSectionProps {
  children: React.ReactNode;
  /**
   * 섹션(잠금 그룹) 모달 전용 비밀번호.
   * `ProtectedPage`의 `password`와는 별도로 두면 됩니다(서로 다른 평문을 `hashPassword`한 값을 넣으세요).
   */
  lockPassword: string | string[];
  description?: string;
  /** 블러 위에 보이는 짧은 안내 (기본: 탭하여 내용 열기) */
  hintText?: string;
}

/**
 * 임의의 자식 블록을 한 덩어리로 잠급니다. 블러 후 탭하면 비밀번호 모달이 열리고,
 * 인증되면 블러가 제거됩니다. (세션 동안만 유지 — 새로고침 시 다시 잠김)
 * 글 본문에서는 `ProtectedArticleGroup`으로 감싸는 편이 의도가 더 분명합니다.
 */
const ProtectedSection: React.FC<ProtectedSectionProps> = ({
  children,
  lockPassword,
  description,
  hintText = 'Private',
}) => {
  const [unlocked, setUnlocked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileHintVisible, setIsMobileHintVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const handlePasswordVerification = useCallback(
    (inputPassword: string): boolean => {
      if (Array.isArray(lockPassword)) {
        return lockPassword.some((pwd) => verifyPassword(inputPassword, pwd));
      }
      return verifyPassword(inputPassword, lockPassword);
    },
    [lockPassword]
  );

  const handleSuccess = useCallback(() => {
    setUnlocked(true);
    setModalOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const applyViewport = () => {
      const mobile = mediaQuery.matches;
      setIsMobileViewport(mobile);
      if (!mobile) {
        setIsMobileHintVisible(false);
      }
    };

    applyViewport();
    mediaQuery.addEventListener('change', applyViewport);
    return () => mediaQuery.removeEventListener('change', applyViewport);
  }, []);

  useEffect(() => {
    if (!isMobileViewport || !sectionRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const viewportBottom = window.innerHeight;
        const bottomBandTop = viewportBottom - 80;
        const sectionTop = entry.boundingClientRect.top;
        const sectionBottom = entry.boundingClientRect.bottom;
        const intersectsBottomBand =
          sectionBottom > bottomBandTop && sectionTop < viewportBottom;

        setIsMobileHintVisible(entry.isIntersecting && intersectsBottomBand);
      },
      {
        threshold: [0, 0.01, 1],
      }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [isMobileViewport]);

  if (unlocked) {
    return <>{children}</>;
  }

  return (
    <div className="protected-section" ref={sectionRef}>
      <div className="protected-section__inner" aria-hidden>
        {children}
      </div>
      <button
        type="button"
        className="protected-section__veil"
        onClick={() => setModalOpen(true)}
        aria-label="잠긴 섹션입니다. 비밀번호를 입력하려면 누르세요"
      >
        <span
          className={
            'protected-section__hint-stack' +
            (isMobileHintVisible ? ' protected-section__hint-stack--mobile-visible' : '')
          }
        >
          <IconLock
            className="protected-section__hint-icon"
            width={18}
            height={18}
            aria-hidden
          />
          <span className="protected-section__hint">{hintText}</span>
        </span>
      </button>
      <PasswordModal
        isOpen={modalOpen}
        onSuccess={handleSuccess}
        correctPassword={lockPassword}
        verifyPassword={handlePasswordVerification}
        description={description}
        onDismiss={() => setModalOpen(false)}
        variant="section"
        modalTitle={
          <>
            이 내용을 보려면,
            <br />
            알맞은 답을 입력해주세요
          </>
        }
      />
    </div>
  );
};

export default ProtectedSection;
